import { useState, useEffect } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, FolderOpen, LogOut, Loader2, Sparkles, Trash2, MoreVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface ProjectSelectorProps {
  onProjectSelected: () => void
}

export function ProjectSelector({ onProjectSelected }: ProjectSelectorProps) {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [createError, setCreateError] = useState('')
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const { user, logout, currentAccount } = useAuthStore()
  const {
    projects,
    isLoading,
    error,
    loadProjects,
    selectProject,
    createProject,
    deleteProject,
    clearError,
  } = useProjectStore()

  useEffect(() => {
    if (currentAccount) {
      loadProjects(currentAccount.id)
    }
  }, [currentAccount, loadProjects])

  // Auto-generate slug from name
  useEffect(() => {
    const slug = newName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    setNewSlug(slug)
  }, [newName])

  const handleSelectProject = async (projectId: string) => {
    if (!currentAccount) return
    try {
      await selectProject(currentAccount.id, projectId)
      onProjectSelected()
    } catch {
      // Error handled by store
    }
  }

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError('')
    clearError()

    if (!currentAccount) {
      setCreateError('No account selected')
      return
    }

    if (!newSlug) {
      setCreateError('Please enter a project name')
      return
    }

    try {
      await createProject(currentAccount.id, newName, newSlug, newDescription || undefined)
      onProjectSelected()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create project')
    }
  }

  const handleLogout = async () => {
    await logout()
  }

  const handleDeleteProject = async () => {
    if (!currentAccount || !deleteProjectId) return
    setIsDeleting(true)
    try {
      await deleteProject(currentAccount.id, deleteProjectId)
      setDeleteProjectId(null)
    } catch {
      // Error handled by store
    } finally {
      setIsDeleting(false)
    }
  }

  const projectToDelete = projects.find(p => p.id === deleteProjectId)

  if (isLoading && projects.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-warm-100 rounded-2xl">
        <Loader2 className="w-8 h-8 text-solution animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full w-full bg-warm-100 rounded-2xl overflow-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-solution flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text">Your Projects</h1>
              <p className="text-text-secondary">
                Welcome back, {user?.name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-text-secondary hover:text-text"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
            {error}
          </div>
        )}

        {/* Create New Project */}
        {showCreateForm ? (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Create New Project</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateProject} className="space-y-4">
                {createError && (
                  <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
                    {createError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text mb-1.5">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-3 py-2 bg-warm-50 border border-border rounded-lg text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="My Product"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-1.5">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={newSlug}
                    onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="w-full px-3 py-2 bg-warm-50 border border-border rounded-lg text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="my-product"
                    pattern="[a-z0-9-]+"
                    required
                  />
                  <p className="mt-1 text-xs text-text-muted">
                    Used in URLs. Lowercase letters, numbers, and hyphens only.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text mb-1.5">
                    Description (optional)
                  </label>
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="w-full px-3 py-2 bg-warm-50 border border-border rounded-lg text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    placeholder="A brief description of your project"
                    rows={2}
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Project'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowCreateForm(false)
                      setNewName('')
                      setNewSlug('')
                      setNewDescription('')
                      setCreateError('')
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <button
            onClick={() => setShowCreateForm(true)}
            className="mb-8 w-full p-4 border-2 border-dashed border-border hover:border-primary/50 rounded-xl text-text-secondary hover:text-text transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create New Project
          </button>
        )}

        {/* Project List */}
        {projects.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {projects.map((project) => (
              <div
                key={project.id}
                className="p-6 bg-surface hover:bg-warm-50 border border-border hover:border-primary/30 rounded-xl text-left transition-colors shadow-sm relative group"
              >
                <button
                  onClick={() => handleSelectProject(project.id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-solution/10 rounded-lg">
                      <FolderOpen className="w-6 h-6 text-solution" />
                    </div>
                    <div className="flex-1 min-w-0 pr-8">
                      <h3 className="font-semibold text-text truncate">
                        {project.name}
                      </h3>
                      <p className="text-sm text-text-muted truncate">
                        {project.slug}
                      </p>
                      {project.description && (
                        <p className="mt-2 text-sm text-text-secondary line-clamp-2">
                          {project.description}
                        </p>
                      )}
                      <div className="mt-3">
                        <Badge variant="secondary">
                          {project.role}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </button>
                {project.role === 'admin' && (
                  <div className="absolute top-4 right-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-error focus:text-error"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteProjectId(project.id)
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Project
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-warm-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-text-muted" />
            </div>
            <p className="text-text-secondary font-medium">No projects yet</p>
            <p className="text-text-muted text-sm mt-1">
              Create your first project to get started
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProjectId} onOpenChange={(open) => !open && setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{projectToDelete?.name}</strong>?
              This will permanently delete all project data including ontology data, customers, and contacts.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={isDeleting}
              className="bg-error hover:bg-error/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Project'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
